import { Link } from "react-router-dom";
import { useEffect } from "react";
import SellNavbar from "@/components/sell/SellNavbar";
import SellHero from "@/components/sell/SellHero";
import SellTargetAudience from "@/components/sell/SellTargetAudience";
import SellFeatures from "@/components/sell/SellFeatures";
import SellResults from "@/components/sell/SellResults";
import SellProcess from "@/components/sell/SellProcess";
import SellBeforeAfter from "@/components/sell/SellBeforeAfter";
import SellSections from "@/components/sell/SellSections";
import SellGallery from "@/components/sell/SellGallery";
import SellGuarantee from "@/components/sell/SellGuarantee";
import SellTestimonials from "@/components/sell/SellTestimonials";
import SellPricing from "@/components/sell/SellPricing";
import SellComparison from "@/components/sell/SellComparison";
import SellPerformance from "@/components/sell/SellPerformance";
import SellROICalculator from "@/components/sell/SellROICalculator";
import SellFAQ from "@/components/sell/SellFAQ";
import SellContact from "@/components/sell/SellContact";
import SellStickyMobileCTA from "@/components/sell/SellStickyMobileCTA";
import SellFloatingContact from "@/components/sell/SellFloatingContact";
import SellFomoToasts from "@/components/sell/SellFomoToasts";
import SellExitIntent from "@/components/sell/SellExitIntent";
import SellScrollProgress from "@/components/sell/SellScrollProgress";
import SellCookieConsent from "@/components/sell/SellCookieConsent";
import SellCountdownBanner from "@/components/sell/SellCountdownBanner";
import SellVideoTestimonial from "@/components/sell/SellVideoTestimonial";
import SellTrustStrip from "@/components/sell/SellTrustStrip";
import SellCaseStudy from "@/components/sell/SellCaseStudy";
import SellPressSection from "@/components/sell/SellPressSection";
import SellLeadMagnet from "@/components/sell/SellLeadMagnet";
import SellPersonalizer from "@/components/sell/SellPersonalizer";
import SellAnalyticsPreview from "@/components/sell/SellAnalyticsPreview";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, Clock, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const sellJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Folio Studio — Création de Portfolio Professionnel",
  "description": "Service de création de portfolios professionnels haut de gamme. Design premium, animations fluides, visible sur Google, livré en 48h. À partir de 79€.",
  "provider": {
    "@type": "ProfessionalService",
    "name": "Folio Studio",
    "url": "https://foliostudio.fr/sell"
  },
  "areaServed": "FR",
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Formules Portfolio",
    "itemListElement": [
      {
        "@type": "Offer",
        "name": "Starter",
        "price": "79",
        "priceCurrency": "EUR",
        "description": "Personnalisation basique, prêt à publier sous 72h"
      },
      {
        "@type": "Offer",
        "name": "Clé en main",
        "price": "149",
        "priceCurrency": "EUR",
        "description": "Portfolio complet et optimisé, bilingue FR/EN, livré en 48h"
      },
      {
        "@type": "Offer",
        "name": "Sur-mesure",
        "price": "349",
        "priceCurrency": "EUR",
        "description": "Portfolio unique avec fonctionnalités custom"
      }
    ]
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "ratingCount": "50",
    "bestRating": "5"
  }
};

const Sell = () => {
  useEffect(() => {
    // Set SEO meta
    document.title = "Folio Studio — Votre Vitrine Professionnelle Haut de Gamme";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Création de portfolios professionnels premium. Design unique, animations fluides, visible sur Google, livré en 48h. À partir de 79€. Satisfait ou remboursé.");
    }

    // Add JSON-LD
    const existingScript = document.querySelector('script[data-sell-jsonld]');
    if (!existingScript) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-sell-jsonld", "true");
      script.textContent = JSON.stringify(sellJsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const s = document.querySelector('script[data-sell-jsonld]');
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden relative">
      {/* ═══ Page-level ambient background ═══ */}
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden="true">
        {/* Top hero glow */}
        <div className="absolute top-[-10%] left-[-15%] w-[800px] h-[800px] bg-primary/[0.07] rounded-full blur-[200px]" />
        <div className="absolute top-[5%] right-[-10%] w-[600px] h-[600px] bg-accent/[0.05] rounded-full blur-[180px]" />
        
        {/* Mid-page accent */}
        <div className="absolute top-[40%] left-[50%] -translate-x-1/2 w-[1000px] h-[400px] bg-primary/[0.03] rounded-full blur-[200px]" />
        
        {/* Bottom warm glow */}
        <div className="absolute bottom-[10%] right-[-5%] w-[700px] h-[700px] bg-accent/[0.04] rounded-full blur-[200px]" />
        <div className="absolute bottom-[-5%] left-[10%] w-[500px] h-[500px] bg-primary/[0.04] rounded-full blur-[180px]" />
        
        {/* Subtle noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.015] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iLjc1IiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIxIi8+PC9zdmc+')]" />
      </div>

      <div className="relative z-10">
        <SellScrollProgress />
        <SellNavbar />
        <SellStickyMobileCTA />
        <SellFloatingContact />
        <SellFomoToasts />
        <SellExitIntent />
        <SellCookieConsent />

      <div className="pt-16">
        <SellCountdownBanner />
        <SellHero />
        <SellTrustStrip />
        <SellPressSection />

        {/* Conversion funnel order optimized */}
        <div id="audience" className="scroll-mt-20"><SellTargetAudience /></div>
        <div id="features" className="scroll-mt-20"><SellFeatures /></div>
        <SellResults />
        <div id="process" className="scroll-mt-20"><SellProcess /></div>
         <div id="compare" className="scroll-mt-20"><SellBeforeAfter /></div>
        <SellSections />
        <div id="gallery" className="scroll-mt-20"><SellGallery /></div>
        
        {/* Live Personalizer — interactive engagement */}
        <div id="personalizer" className="scroll-mt-20"><SellPersonalizer /></div>
        
        {/* Case Study — real proof before pricing */}
        <div id="case-study" className="scroll-mt-20"><SellCaseStudy /></div>
        
        {/* Analytics Preview — show potential */}
        <SellAnalyticsPreview />
        
        {/* Performance proof — before pricing to justify value */}
        <div id="performance" className="scroll-mt-20"><SellPerformance /></div>
        
        {/* Comparison table — removes competitor objections before pricing */}
        <div id="comparison" className="scroll-mt-20"><SellComparison /></div>
        
        {/* ROI Calculator — quantifies the value before pricing */}
        <div id="roi" className="scroll-mt-20"><SellROICalculator /></div>
        
        {/* Lead Magnet — capture hesitant visitors before pricing */}
        <SellLeadMagnet />
        
        <SellGuarantee />
        <SellVideoTestimonial />
        <div id="testimonials" className="scroll-mt-20"><SellTestimonials /></div>
        <SellPricing />
        <div id="faq" className="scroll-mt-20"><SellFAQ /></div>

        {/* Final CTA — high impact */}
        <section className="py-24">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto text-center p-12 md:p-16 rounded-3xl bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border border-primary/20 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-60 h-60 bg-primary/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-60 h-60 bg-accent/10 rounded-full blur-3xl" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
              
              <div className="relative">
                <motion.div 
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-6"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  <Sparkles className="w-4 h-4" />
                  Offre de lancement — places limitées
                </motion.div>

                <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-6 leading-tight">
                  Ne laissez plus vos concurrents
                  <br />
                  <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    vous voler des clients
                  </span>
                </h2>
                <p className="text-muted-foreground text-base sm:text-lg mb-4 max-w-xl mx-auto">
                  Chaque jour sans portfolio premium, c'est un prospect qui choisit un concurrent avec une meilleure image en ligne.
                </p>
                <div className="flex flex-wrap justify-center gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground mb-8">
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-primary" />
                    Satisfait ou remboursé
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-primary" />
                    Livré en 48h
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    4.9/5 de satisfaction
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="#contact">
                    <Button size="lg" className="text-base sm:text-lg px-8 sm:px-12 py-6 sm:py-7 bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-lg shadow-primary/25 group w-full sm:w-auto">
                      Oui, je veux mon portfolio maintenant
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                  <a href="/cv" target="_blank" rel="noopener">
                    <Button size="lg" variant="outline" className="text-base sm:text-lg px-8 py-6 sm:py-7 border-primary/30 hover:bg-primary/10 w-full sm:w-auto">
                      Voir la démo live
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <div id="contact" className="scroll-mt-20">
          <SellContact />
        </div>

        {/* Premium Footer */}
        <footer className="py-16 border-t border-border/20">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-12 mb-12">
              <div>
                <div className="flex items-center gap-2 font-bold text-lg mb-4">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    Folio Studio
                  </span>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  Création de portfolios professionnels haut de gamme pour tous les métiers. 
                  Design premium, livré en 48h, visible sur Google.
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="font-medium">4.9/5</span>
                  <span>— 50+ clients satisfaits</span>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-4">Navigation</h4>
                <ul className="space-y-2.5 text-sm text-muted-foreground">
                  {[
                    { href: "#features", label: "Fonctionnalités" },
                    { href: "#process", label: "Comment ça marche" },
                    { href: "#gallery", label: "Exemples par secteur" },
                    { href: "#performance", label: "Performances" },
                    { href: "#comparison", label: "Comparatif" },
                    { href: "#roi", label: "Calculateur ROI" },
                    { href: "#pricing", label: "Tarifs" },
                    { href: "#faq", label: "FAQ" },
                     { href: "#contact", label: "Contact" },
                   ].map(l => (
                    <li key={l.href}>
                      <a href={l.href} className="hover:text-primary transition-colors">{l.label}</a>
                    </li>
                  ))}
                  <li>
                    <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-4">Confiance & Sécurité</h4>
                <ul className="space-y-2.5 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-primary" />
                    Paiement sécurisé (Stripe)
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-primary" />
                    Données chiffrées (SSL)
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-primary" />
                    Conforme RGPD
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-primary" />
                    Garantie satisfait ou remboursé
                  </li>
                </ul>
                <Link 
                  to="/legal" 
                  className="inline-block mt-4 text-sm text-primary hover:underline underline-offset-4"
                >
                  Mentions légales & CGV →
                </Link>
              </div>
            </div>

            <div className="border-t border-border/30 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
              <span>© {new Date().getFullYear()} Folio Studio. Tous droits réservés.</span>
              <a href="/cv" target="_blank" rel="noopener" className="text-primary hover:underline underline-offset-4">
                Voir la démo live →
              </a>
            </div>
          </div>
        </footer>
      </div>
      </div>
    </div>
  );
};

export default Sell;
